import { createActivityLog, type ActivityLog } from './activity-log';
import { observeUserActivity, type DomRecorderOptions } from './dom-recorder';
import { createWidgetActivitySink, type WidgetActivitySink } from './http-recorder';
import { selectActivity } from './snapshot';
import type { ActivityEntry, ActivitySubject } from './types';
import { observeWidgetLoads } from './widget-load-recorder';

/** What the failure reporter asks of the activity: the entries to send beside a failure. */
export interface ActivitySource {
  snapshotFor(subject: ActivitySubject, now?: number): ActivityEntry[];
}

export interface WidgetActivity extends ActivitySource {
  /** Bound as `window.sharedHostGateway.activity`; platform-api reports widget requests to it. */
  sink: WidgetActivitySink;
  /** The router tells it where the page went (`RouteActivity` in app.tsx). */
  routeChanged(path: string): void;
  /** Stops recording; the log keeps what it has. */
  stop(): void;
}

export interface InstallOptions extends DomRecorderOptions {
  log?: ActivityLog;
}

/**
 * Starts recording: user events on the window, widget loads from the loader's events, and
 * whatever the gateway sink and the router report. One per page, created in `main.tsx`.
 */
export function installWidgetActivity({
  log = createActivityLog(),
  target = window,
  now = Date.now
}: InstallOptions = {}): WidgetActivity {
  const stopDom = observeUserActivity(log.record, { target, now });
  const stopLoads = observeWidgetLoads(log.record, now);
  return {
    sink: createWidgetActivitySink(log.record, now),
    routeChanged(path) {
      log.record({ kind: 'route', time: now(), path });
    },
    snapshotFor(subject, at = now()) {
      return selectActivity(log.recent(at), subject);
    },
    stop() {
      stopDom();
      stopLoads();
    }
  };
}
